import React from 'react'
import styled from 'styled-components'
import { isEmpty } from 'ramda'
import { Box } from '@ally/metronome-ui'
import useMboxCall from '@ally/use-mbox-call'
import { StatusTrackerActivity } from '@ally/shared-status-tracker'
import { useHostServices } from '../HostServices'
import { fetchActivitiesData } from './tipsData'
import StatusTrackerTips from './StatusTrackerTips/StatusTrackerTips'
import { filterActivities, sortActivities, verifyContent } from '../utils'
import { statusTrackerAnalytics } from '../analytics'
import TipsMbox from './TipsMbox'

const Wrapper = styled(Box)`
  ${({ theme: { colors, space } }): string => `
    padding: ${space.sm};
    margin-bottom: ${space.md};
    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.16), 0 5px 8px -4px rgba(0, 0, 0, 0.16);
    border: 1px solid ${colors['gray-300']};
    border-radius: 4px;
  `};
`

export const List = styled.ul`
  ${({ theme: { colors, space } }): string => `
  padding: 0;
  margin: 0;
  line-height: 1.5;

    & ~ & {
      margin-top: ${space.sm};
      padding-top: ${space.sm};
      border-top: 1px solid ${colors['slate-2']};
    }
  `};
`

export const ListItem = styled.li`
  ${({ theme: { colors, space } }): string => `
    list-style: none;
    display: flex;
    padding: ${space.sm} 0;
    border-bottom: 1px solid ${colors['slate-2']};

    &:first-of-type {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
      border-bottom: none;
    }
  `};
`

const TipsAndOpportunities: React.FC = () => {
  const [trackingFired, setTrackingFired] = React.useState(false)
  const [activities, setActivities] = React.useState<
    StatusTrackerActivity[] | []
  >([])
  const { session, featureFlags, analytics, log } = useHostServices()

  const { loadState } = featureFlags
  const guid = session.data?.guid
  const isLoaded = loadState === 'loaded'

  React.useEffect(() => {
    if (isLoaded && guid && guid !== 'null') {
      fetchActivitiesData({ guid, session, featureFlags }).then(data => {
        const filteredActivities = filterActivities(data, log)
        const sortedActivities = sortActivities(filteredActivities)
        setActivities(sortedActivities.slice(0, 2))
      })
    }
  }, [guid, isLoaded])

  React.useEffect(() => {
    if (!trackingFired && analytics && activities.length) {
      statusTrackerAnalytics(analytics, activities)
      setTrackingFired(true)
    }
  }, [analytics, activities])

  const adobeTip1 = useMboxCall('ally-dashboard-tips-01', []).mboxData
  const adobeTip2 = useMboxCall('ally-dashboard-tips-02', []).mboxData
  const adobeTip3 = useMboxCall('ally-dashboard-tips-03', []).mboxData

  if (adobeTip1 && !isEmpty(adobeTip1)) {
    adobeTip1[0].trackingName = 'ally-dashboard-tips-01'
  }

  if (adobeTip2 && !isEmpty(adobeTip2)) {
    adobeTip2[0].trackingName = 'ally-dashboard-tips-02'
  }

  if (adobeTip3 && !isEmpty(adobeTip3)) {
    adobeTip3[0].trackingName = 'ally-dashboard-tips-03'
  }

  const tipArray = [adobeTip1, adobeTip2, adobeTip3]
  const filteredTips = tipArray.filter(tip => {
    try {
      return verifyContent(tip)
    } catch {
      // istanbul ignore next
      return false
    }
  })

  return (
    <>
      {activities.length > 0 || filteredTips.length > 0 ? (
        <Wrapper>
          {filteredTips.length > 0 && <TipsMbox adobeTips={filteredTips} />}
          {activities.length > 0 && (
            <StatusTrackerTips activities={activities} />
          )}
        </Wrapper>
      ) : null}
    </>
  )
}

export default TipsAndOpportunities
